import { useState } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  FormGroup,
  FormLabel,
  TextField,
  LinearProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import FileCopyIcon from "@mui/icons-material/FileCopy";

import MaskAndValid from "utils/maskAndValid";
import Api from "lib/api/httpRequestHelper";
import useLogs from "hooks/logs";
import componentButtonStyles from "assets/theme/components/cards/sortable/card-light-table-sortable";
import { IS_PROD_ENV } from "constants/environment";

const useComponentStyles = makeStyles(componentButtonStyles);

const BASE_URL = IS_PROD_ENV ? "karsten.digital" : "hml.karsten.digital";

const MAX_NAME_LENGTH = 150;
const MAX_PHONE_LENGTH = 50;
const MAX_CPF_LENGTH = 14;

export default function DialogSendShowcase({ data, handleClose }) {
  const classes = { ...useComponentStyles() };
  const { setLocalStorageActions, setLocalStorageApiProblem } = useLogs();
  console.log("data", data);
  const [formData, setFormData] = useState({
    name: "",
    cpf: "",
    phone: "",
  });

  const [sendingShowcase, setSendingShowcase] = useState(false);
  const [showLink, setShowLink] = useState(false);
  const [link, setLink] = useState();

  const handleInputChange = (field) => (event) => {
    const value = event.target.value;
    let formattedValue = value;

    switch (field) {
      case "phone":
        formattedValue = MaskAndValid.maskPhone(value);
        break;
      case "cpf":
        formattedValue =
          value.length <= 13
            ? MaskAndValid.maskCPF(value)
            : value.length > 14
            ? MaskAndValid.maskCNPJ(value)
            : value;
        break;
      default:
        formattedValue = value;
    }

    setFormData((prev) => ({
      ...prev,
      [field]: formattedValue,
    }));
  };

  const isPrivate = !data?.VitrinePublica;
  console.log("isPrivate", isPrivate);

  const handleSendShowCase = async () => {
    setShowLink(false);
    setSendingShowcase(true);

    setLocalStorageActions({
      nomeTela: "Vitrine Virtual",
      idMenu: "159",
      acao: "Clicou para Criar link de vitrine virtual para cliente",
    });

    try {
      if (isPrivate) {
        const { Result } = await Api.post(
          "create-virtual-customer",
          "showcase",
          {
            idVitrineVirtual: data?.IdVitrineVirtual?.toString(),
            idLojaVendedor: data?.IdLojaVendedor,
            nome: formData.name,
            cpf: formData.cpf.replace(/[^\d]+/g, ""),
            telefone: formData.phone.replace(/[^\d]+/g, ""),
          },
          true
        );
        if (Result?.HashVitrine) {
          const cpfDigits = formData.cpf.replace(/[^\d]+/g, "");
          const redirectPage = `https://${BASE_URL}/vitrine-virtual-cliente?hashVitrine=${Result?.HashVitrine}&cpf=${cpfDigits}`;

          const whatsappLink = `https://wa.me/55${formData.phone.replace(
            /[^\d]+/g,
            ""
          )}?text=Visite a vitrine virtual criada para você! ${encodeURIComponent(
            redirectPage
          )}`;

          setShowLink(true);
          setLink(whatsappLink);
        }
      } else {
        const redirectPage = `https://${BASE_URL}/vitrine-virtual-cliente?hashVitrine=${data.TokenVitrine}`;
        setShowLink(true);
        setLink(redirectPage);
      }
    } catch (error) {
      console.error(error);
      setLocalStorageApiProblem({
        idMenuAccess: "159",
        screen: "Vitrine Virtual",
        problem: `Erro na chamada do endpoint create-virtual-customer - ${error}`,
      });
    } finally {
      setSendingShowcase(false);
    }
  };

  const isFormValid = () => {
    const clientFieldsValid =
      !isPrivate ||
      (formData.phone &&
        formData.phone.length === 15 &&
        formData.cpf &&
        formData.cpf.length === 14 &&
        formData.name);

    return clientFieldsValid && !showLink;
  };

  return (
    <Dialog
      maxWidth="lg"
      open
      onClose={handleClose}
      aria-labelledby="alert-dialog-title"
    >
      <DialogTitle>
        <Typography variant="h3" component="h3">
          Gerar Link da Vitrine Virtual <strong>{data?.Nome}</strong> para
          Cliente
        </Typography>
      </DialogTitle>

      <PerfectScrollbar>
        <DialogContent style={{ padding: "0 2rem" }}>
          <Grid container spacing={2}>
            {isPrivate && (
              <>
                <Grid item xs={12}>
                  <FormGroup>
                    <FormLabel>Nome *</FormLabel>
                    <TextField
                      name="name"
                      required
                      fullWidth
                      disabled={sendingShowcase || showLink}
                      placeholder="Nome do Cliente"
                      value={formData.name}
                      variant="outlined"
                      onChange={handleInputChange("name")}
                      onInput={(e) => {
                        e.target.value = e.target.value
                          .toString()
                          .slice(0, MAX_NAME_LENGTH);
                      }}
                    />
                  </FormGroup>
                </Grid>

                <Grid item xs={12}>
                  <FormGroup>
                    <FormLabel>CPF *</FormLabel>
                    <TextField
                      name="cpf"
                      required
                      fullWidth
                      disabled={sendingShowcase || showLink}
                      placeholder="CPF do Cliente"
                      value={formData.cpf}
                      variant="outlined"
                      onChange={handleInputChange("cpf")}
                      onInput={(e) => {
                        e.target.value = e.target.value
                          .toString()
                          .slice(0, MAX_CPF_LENGTH);
                      }}
                    />
                  </FormGroup>
                </Grid>

                <Grid item xs={12}>
                  <FormGroup>
                    <FormLabel>Telefone *</FormLabel>
                    <TextField
                      name="phone"
                      required
                      fullWidth
                      disabled={sendingShowcase || showLink}
                      placeholder="Telefone do Cliente"
                      value={formData.phone}
                      variant="outlined"
                      onChange={handleInputChange("phone")}
                      onInput={(e) => {
                        e.target.value = e.target.value
                          .toString()
                          .slice(0, MAX_PHONE_LENGTH);
                      }}
                    />
                  </FormGroup>
                </Grid>
              </>
            )}

            {showLink && (
              <Grid item xs={12}>
                {isPrivate ? (
                  <a
                    href={link}
                    target="_blank"
                    style={{ color: "inherit", cursor: "pointer" }}
                    rel="noreferrer"
                  >
                    <FormGroup
                      style={{
                        display: "flex",
                        alignItems: "center",
                        flexDirection: "row",
                      }}
                    >
                      <FormLabel style={{ cursor: "pointer" }}>
                        Clique para enviar o link criado
                        <Box
                          component={WhatsAppIcon}
                          position="relative"
                          color="green"
                          marginLeft="0.5rem"
                          cursor="pointer"
                          width={"1.25rem!important"}
                          height={"1.25rem!important"}
                        />
                      </FormLabel>
                    </FormGroup>
                  </a>
                ) : (
                  <FormGroup
                    style={{
                      display: "flex",
                      alignItems: "center",
                      flexDirection: "row",
                    }}
                  >
                    <FormLabel style={{ cursor: "pointer" }}>
                      Copiar link para compartilhamento
                      <Box
                        component={FileCopyIcon}
                        position="relative"
                        color="primary"
                        marginLeft="0.5rem"
                        cursor="pointer"
                        width={"1.25rem!important"}
                        height={"1.25rem!important"}
                        onClick={() => {
                          navigator.clipboard.writeText(link);
                        }}
                      />
                    </FormLabel>
                  </FormGroup>
                )}
              </Grid>
            )}
          </Grid>
        </DialogContent>
      </PerfectScrollbar>

      <DialogActions>
        <Button
          variant="contained"
          onClick={handleSendShowCase}
          disabled={!isFormValid() || sendingShowcase}
        >
          {sendingShowcase ? "Criando link..." : "Criar link"}
        </Button>
        <Button
          component={Box}
          onClick={handleClose}
          disabled={sendingShowcase}
        >
          Cancelar
        </Button>
      </DialogActions>

      <Grid
        item
        xs={12}
        style={{ marginBottom: "2rem" }}
        hidden={!sendingShowcase}
      >
        <div
          className={classes.root}
          style={{ marginTop: "1.5rem", marginBottom: "-1rem" }}
        >
          <span>Criando link para a vitrine virtual...</span>
          <LinearProgress />
        </div>
      </Grid>
    </Dialog>
  );
}
